import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
// Swiper is registered in the visitor sign-in page, the only place that renders a
// swiper-container. Registering it here pulled roughly 0.3 MB into the initial bundle.
import { ModalController, Platform } from '@ionic/angular';
import { SentryService } from './services/sentry.service';
import { StorageService } from './services/storage.service';
import { AppService } from './services/app.service';
import { RegionSelectionComponent } from './components/region-selection/region-selection.component';
import { UtilsService } from './services/utils.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit {
  constructor(
    private platform: Platform,
    private sentry: SentryService,
    private storage: StorageService,
    private app: AppService,
    private modalCtrl: ModalController,
    private utils: UtilsService
  ) {}

  ngOnInit() {
    // When the platform is ready, perform some actions.
    this.platform.ready().then(async (): Promise<void> => {
      // Get the region to determine which URL to use.
      const region: any = this.app.getRegion();
      if (!region) {
        // Hide the splash screen.
        SplashScreen.hide();

        if (
          this.utils.getEnvironmentConfig('app_name') == 'SiteConnect Kiosk'
        ) {
          // Open the region selector.
          const regionSelector = await this.modalCtrl.create({
            component: RegionSelectionComponent,
          });
          await regionSelector.present();
          const { data } = await regionSelector.onWillDismiss(); // The selected region. ANZ or UK.
          this.bootstrapApp(data);
        } else {
          // Take5™ Connect & SetConnect
          this.app.region = 'ANZ';
          this.storage.set('Region', this.app.region);
          this.bootstrapApp(this.app.region);
        }
      } else {
        // Hide the splash screen.
        SplashScreen.hide();
        this.bootstrapApp(region);
      }
    });
  }

  /**
   * Bootstraps the application by preparing the necessary configuration for the specified region,
   * initializing third-party services, and setting the UI appearance.
   *
   * @param {string} region - The region code used to configure the application.
   * @return {Promise<void>} A promise that resolves when the bootstrap process is complete.
   */
  async bootstrapApp(region: string): Promise<void> {
    // Get the API URL from using storage. This must be called after this.storage.init() and before any other API operations.
    this.app.prepareAPIURL(region);
    // Initialize Sentry
    this.sentry
      .allowInit()
      .then(async (allowSentry: boolean): Promise<void> => {
        if (allowSentry) {
          this.sentry.onInit();
        }
      });
    // Set the status bar style to Light.
    StatusBar.setStyle({ style: Style.Light });
  }
}
